import { useMemo, useState } from 'react'
import { CheckboxGroup, SelectGroup } from '@/common'
import useI18n from '@/shared/hooks/useI18n'
import {
  differenceInMonths,
  endOfYear,
  format,
  getYear,
  startOfDay,
  startOfMonth,
  startOfYear,
  subMonths
} from 'date-fns'

type PeriodSelectorProps = {
  startDate: string | null
  endDate: string | null
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  className?: string
  notCalendarYearPeriodLabel: React.ReactNode
  disabledNonCalendarYearPeriod?: boolean
  minMonths?: number // Minimum number of months for the period (default: 3)
  maxMonths?: number // Maximum number of months for the period (default: 12)
}

const getLast32Months = () => {
  const months = []
  const now = startOfMonth(startOfDay(new Date()))
  for (let i = 0; i < 32; i++) {
    const date = startOfMonth(subMonths(now, i))
    months.push(format(date, 'yyyy-MM'))
  }
  return months
}

const getAvailableYears = () => {
  const last32Months = getLast32Months()
  const currentYear = getYear(new Date())
  const years = [...new Set(last32Months.map(date => getYear(new Date(date))))]
    .filter(year => year < currentYear)
    .sort((prev, next) => next - prev)
  return years
}

const getAvailableYearsFilteredByMinPeriod = (minMonths: number = 3) => {
  const availableYears = getAvailableYears()
  const now = new Date()

  return availableYears.filter(year => {
    // For calendar years, we need to check if the full year (12 months) fits within our constraints
    // But only if minMonths > 12, we need to filter out years
    if (minMonths <= 12) {
      return true // All years are valid if minimum period is 12 months or less
    }

    // If minMonths > 12, check if there are enough months from January of this year
    const requiredEndDate = new Date(year, minMonths - 1, 1) // Required end month

    return requiredEndDate <= now
  })
}

const getOptionsForEndMonthFilteredBasedOnStartDate = (
  startDate: string,
  minMonths: number = 3,
  maxMonths: number = 12
) => {
  const startMonth = new Date(startDate + '-01') // Add day to make it a valid date
  const last32Months = getLast32Months()
  return last32Months.filter(month => {
    const endMonth = new Date(month + '-01') // Convert string to Date
    const monthDiff = differenceInMonths(endMonth, startMonth)
    return monthDiff >= minMonths - 1 && monthDiff <= maxMonths - 1
  })
}

const getOptionsForStartMonthFilteredByMinPeriod = (minMonths: number = 3) => {
  const last32Months = getLast32Months()
  const now = new Date()

  return last32Months.filter(month => {
    const startMonth = new Date(month + '-01') // Convert string to Date
    // Check if there are enough months available from this start month to satisfy minMonths requirement
    const potentialEndMonth = new Date(
      startMonth.getFullYear(),
      startMonth.getMonth() + minMonths - 1,
      1
    )

    // Make sure the potential end month doesn't exceed current month
    return potentialEndMonth <= now
  })
}

export default function PeriodSelector({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  className = '',
  notCalendarYearPeriodLabel,
  minMonths = 3,
  maxMonths = 12,
  disabledNonCalendarYearPeriod = false
}: PeriodSelectorProps) {
  const { t, dateFormatterMonthYear } = useI18n()
  const [nonCalendarYearPeriod, setNonCalendarYearPeriod] = useState(false)

  const handleCalendarYearChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedYear = parseInt(evt.currentTarget.value)
    const yearStart = startOfYear(new Date(selectedYear, 0, 1))
    const yearEnd = endOfYear(new Date(selectedYear, 0, 1))

    onStartDateChange(format(yearStart, 'yyyy-MM'))
    onEndDateChange(format(yearEnd, 'yyyy-MM'))
  }

  const handleStartDateChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    onStartDateChange(evt.currentTarget.value)
    onEndDateChange('') // Reset end date when start date changes
  }

  const optionsForStartMonthFiltered = useMemo(
    () => getOptionsForStartMonthFilteredByMinPeriod(minMonths),
    [minMonths]
  )

  const availableYearsFiltered = useMemo(
    () => getAvailableYearsFilteredByMinPeriod(minMonths),
    [minMonths]
  )

  const optionsForEndMonthFilteredBasedOnStartDate = useMemo(
    () =>
      startDate
        ? getOptionsForEndMonthFilteredBasedOnStartDate(startDate, minMonths, maxMonths)
        : getLast32Months(),
    [startDate, minMonths, maxMonths]
  )

  const onSetNonCalendarYearPeriod = (checked: boolean) => {
    setNonCalendarYearPeriod(checked)
    if (!checked) {
      onStartDateChange('')
      onEndDateChange('')
    }
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {nonCalendarYearPeriod && !disabledNonCalendarYearPeriod ? (
        <div className="grid grid-cols-2 gap-4">
          <SelectGroup
            label={t('common:inputFields.startDate.label')}
            options={optionsForStartMonthFiltered.map(month => ({
              label: dateFormatterMonthYear(new Date(month)),
              value: month
            }))}
            value={startDate ?? ''}
            onChange={handleStartDateChange}
          />

          <SelectGroup
            label={t('common:inputFields.endDate.label')}
            options={optionsForEndMonthFilteredBasedOnStartDate.map(month => ({
              label: dateFormatterMonthYear(new Date(month)),
              value: month
            }))}
            value={endDate ?? ''}
            onChange={evt => onEndDateChange(evt.currentTarget.value)}
          />
        </div>
      ) : (
        <div>
          <SelectGroup
            label={t('common:inputFields.calendarYear.label')}
            options={availableYearsFiltered.map(year => ({
              label: year.toString(),
              value: year.toString()
            }))}
            value={startDate ? new Date(startDate).getFullYear().toString() : ''}
            onChange={handleCalendarYearChange}
          />
        </div>
      )}

      <div>
        <CheckboxGroup
          id="nonCalendarYearPeriod"
          label={notCalendarYearPeriodLabel}
          checked={nonCalendarYearPeriod}
          onChange={onSetNonCalendarYearPeriod}
          disabled={disabledNonCalendarYearPeriod}
        />
      </div>
    </div>
  )
}
